import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Form, message, TimePicker as AntTimePicker, Row, Col, Space, Button } from "antd";
import dayjs from "dayjs";
import JoditEditor from "jodit-react";
import CommonDialog from '../../../global/dialog';
import CommonForm from '../../../global/form';
import CommonTabs from '../../../global/tabs';
import CommonSelect from '../../../global/select';
import CommonCheckbox from '../../../global/checkbox';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import SOURCES from '../../../../utils/constants';
import { useQueryClient } from '@tanstack/react-query';

const EnhancedAddAutoresponders = ({ showForm, setShowForm, editAutomation, keyParams }) => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const editorRef = useRef(null);

    // State management
    const [activeTab, setActiveTab] = useState("1");
    const [autoResponderMessage, setAutoResponderMessage] = useState("");
    const [leadSourceId, setLeadSourceId] = useState([]);
    const [specificDays, setSpecificDays] = useState(false);
    const [timezoneSelected, setTimezoneSelected] = useState(null);
    const [days, setDays] = useState([
        { name: "Monday", day: "Mon", active: false, startTime: "", endTime: "" },
        { name: "Tuesday", day: "Tue", active: false, startTime: "", endTime: "" },
        { name: "Wednesday", day: "Wed", active: false, startTime: "", endTime: "" },
        { name: "Thursday", day: "Thu", active: false, startTime: "", endTime: "" },
        { name: "Friday", day: "Fri", active: false, startTime: "", endTime: "" },
        { name: "Saturday", day: "Sat", active: false, startTime: "", endTime: "" },
        { name: "Sunday", day: "Sun", active: false, startTime: "", endTime: "" },
    ]);

    // Jodit Config
    const config = useMemo(() => ({
        readonly: false,
        height: 400,
        uploader: {
            insertImageAsBase64URI: true,
        },
        extraButtons: [
            {
                name: "First Name",
                tooltip: "Lead First Name",
                exec: (editor) => editor.s.insertHTML("[FIRST_NAME]"),
            },
            {
                name: "Event Type",
                tooltip: "Event Type",
                exec: (editor) => editor.s.insertHTML("[EVENT_TYPE]"),
            },
            {
                name: "Event Date",
                tooltip: "Event Date",
                exec: (editor) => editor.s.insertHTML("[EVENT_DATE]"),
            },
        ],
    }), []);

    // Queries
    const { data: leadSources, isLoading: leadSourcesLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.leadSources.all,
        endpoint: "leadsource",
    });

    const { data: timezones, isLoading: timezonesLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.timezones.all,
        endpoint: "common/timezones",
    });

    const { data: autoresponderDetail, isLoading: autoresponderDetailLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.autoresponders.item({ id: editAutomation.id }),
        queryParams: { id: editAutomation.id },
        endpoint: "autoresponder",
        enabled: !!editAutomation.id && editAutomation.show
    });

    // Mutation
    const saveMutation = useGenericMutation({
        endpoint: "autoresponder",
        onSuccess: () => {
            message.success(editAutomation.id ? "Autoresponder updated successfully." : "Autoresponder added successfully.");
            setShowForm(false);
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.autoresponders.lists(keyParams),
                type: "active"
            });
            queryClient.removeQueries({
                queryKey: QUERY_KEYS.autoresponders.item({ id: editAutomation.id }),
                type: "all"
            })
        },
    });

    // Populate data for editing
    useEffect(() => {
        if (autoresponderDetail && editAutomation.id) {
            form.setFieldsValue({
                senderName: autoresponderDetail.senderName,
                senderEmail: autoresponderDetail.senderEmail,
                title: autoresponderDetail.title,
                name: autoresponderDetail.name,
            });
            setAutoResponderMessage(autoresponderDetail.message || "");
            setTimezoneSelected(autoresponderDetail.timezone || null);

            if (autoresponderDetail.autoresponderSentInfo) {
                setLeadSourceId(autoresponderDetail.autoresponderSentInfo.leadSources || []);
                setSpecificDays(autoresponderDetail.autoresponderSentInfo.sendSpecificDays || false);
                if (autoresponderDetail.autoresponderSentInfo.sendDays) {
                    setDays(autoresponderDetail.autoresponderSentInfo.sendDays);
                }
            }
        }
    }, [autoresponderDetail, editAutomation.id, form]);

    const handleLeadSourceChange = (checked, id) => {
        if (checked) {
            setLeadSourceId(prev => [...prev, id]);
        } else {
            setLeadSourceId(prev => prev.filter(item => item !== id));
        }
    };

    const handleDayAction = (idx, field, value) => {
        const newDays = [...days];
        newDays[idx][field] = value;
        setDays(newDays);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!autoResponderMessage) {
                message.error("Please enter the message content.");
                setActiveTab("4");
                return;
            }

            const autoRespondersRequiredMessageRequiredFields = ['[FIRST_NAME]', '[EVENT_TYPE]', '[EVENT_DATE]'];

            if (autoResponderMessage && !autoRespondersRequiredMessageRequiredFields.every(field => autoResponderMessage.includes(field))) {
                const missingField = autoRespondersRequiredMessageRequiredFields.find(
                    field => !autoResponderMessage.includes(field)
                );
                message.error(`Please include ${missingField} in the message content.`);
                setActiveTab("4");
                return;
            }

            const postData = {
                id: editAutomation.id || null,
                title: values.title,
                name: values.name,
                message: autoResponderMessage,
                timezone: timezoneSelected,
                senderName: values.senderName,
                senderEmail: values.senderEmail,
                autoresponderSentInfo: {
                    leadSources: leadSourceId,
                    sendSpecificDays: specificDays,
                    sendDays: days,
                },
            };

            await saveMutation.mutateAsync(postData);
        } catch (err) {
            console.error("Validation failed:", err);
            // Switch to the first tab if basic info is missing
            if (err.errorFields) {
                setActiveTab("1");
            }
        }
    };

    const timezoneOptions = useMemo(() => {
        if (!timezones || !Array.isArray(timezones)) return [];
        return timezones.map((tz) => ({
            label: `${tz.text} ${tz.utc[0]}`,
            value: tz.utc[0],
        }));
    }, [timezones]);

    const basicFormData = [
        {
            label: "Sender Name",
            name: "senderName",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            placeholder: "Enter sender name",
            required: true,
            message: "Sender name is required.",
            span: 12,
        },
        {
            label: "Sender Email",
            name: "senderEmail",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            placeholder: "Enter sender email",
            required: true,
            message: "A valid sender email is required.",
            type: "email",
            span: 12,
        },
        {
            label: "Autoresponder Subject Title",
            name: "title",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            placeholder: "Enter subject title",
            required: true,
            message: "Subject title is required.",
            span: 12,
        },
        {
            label: "Internal Autoresponder Name",
            name: "name",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            placeholder: "Enter internal name",
            required: true,
            message: "Internal name is required.",
            span: 12,
        }
    ];

    const tabItems = [
        {
            key: "1",
            label: "Basic",
            children: (
                <div className="">
                    <CommonForm
                        data={basicFormData}
                        layoutType="vertical"
                        form={form}
                    />
                </div>
            )
        },
        {
            key: "2",
            label: "Leads Source",
            children: (
                <div className="space-y-4">
                    <h5 className="font-semibold text-sm">Choose Leads Source Job(s)</h5>
                    <div className="grid grid-cols-2 gap-4">
                        {leadSources && Array.isArray(leadSources) && leadSources.map((job) => (
                            <div key={job._id} className="flex items-center gap-3">
                                <CommonCheckbox
                                    checked={leadSourceId.includes(job._id)}
                                    onChange={(e) => handleLeadSourceChange(e.target.checked, job._id)}
                                />
                                <span className="text-sm font-medium text-gray-700">{job.jobName}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )
        },
        {
            key: "3",
            label: "Alert",
            children: (
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">Timezone</label>
                        <CommonSelect
                            options={timezoneOptions}
                            value={timezoneSelected}
                            onChange={(val) => setTimezoneSelected(val)}
                            placeholder="Select timezone"
                            loading={timezonesLoading}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <CommonCheckbox
                            checked={specificDays}
                            onChange={(e) => setSpecificDays(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">
                            Do you want to send autoresponse on a specific schedule?
                        </span>
                    </div>

                    {specificDays && (
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4 max-h-[350px] overflow-y-auto">
                            {days.map((day, idx) => (
                                <Row key={day.day} gutter={16} align="middle" className="pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                                    <Col span={6}>
                                        <div className="flex items-center gap-2">
                                            <CommonCheckbox
                                                checked={day.active}
                                                onChange={(e) => handleDayAction(idx, "active", e.target.checked)}
                                            />
                                            <span className="text-sm font-semibold">{day.name}</span>
                                        </div>
                                    </Col>
                                    <Col span={9}>
                                        <div className="space-y-1">
                                            <label className="text-[10px] uppercase text-gray-400 font-bold block">Start Time</label>
                                            <AntTimePicker
                                                className="w-full h-9 border-gray-300 rounded-md focus:border-primary hover:border-primary"
                                                format="HH:mm"
                                                disabled={!day.active}
                                                value={day.active && day.startTime ? dayjs(day.startTime, "HH:mm") : null}
                                                onChange={(time) => handleDayAction(idx, "startTime", time ? time.format("HH:mm") : "")}
                                            />
                                        </div>
                                    </Col>
                                    <Col span={9}>
                                        <div className="space-y-1">
                                            <label className="text-[10px] uppercase text-gray-400 font-bold block">End Time</label>
                                            <AntTimePicker
                                                className="w-full h-9 border-gray-300 rounded-md focus:border-primary hover:border-primary"
                                                format="HH:mm"
                                                disabled={!day.active}
                                                value={day.active && day.endTime ? dayjs(day.endTime, "HH:mm") : null}
                                                onChange={(time) => handleDayAction(idx, "endTime", time ? time.format("HH:mm") : "")}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            ))}
                        </div>
                    )}
                </div>
            )
        },
        {
            key: "4",
            label: "Detail",
            children: (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                    <JoditEditor
                        ref={editorRef}
                        value={autoResponderMessage}
                        config={config}
                        onBlur={(newContent) => setAutoResponderMessage(newContent)}
                    />
                </div>
            )
        }
    ];

    const isGlobalLoading = leadSourcesLoading || timezonesLoading || (!!editAutomation.id && autoresponderDetailLoading);

    return (
        <CommonDialog
            title={editAutomation.id ? "Edit Autoresponder" : "Create New Autoresponder"}
            description={editAutomation.id ? "Modify the autoresponder details and save your changes." : "Fill the form to create a new automated response."}
            open={showForm}
            setOpen={setShowForm}
            onOk={handleSubmit}
            isConfirmButtonLoading={saveMutation.isPending}
            width={850}
            okButtonTitle={editAutomation.id ? "Update" : "Create"}
            className="relative"
            onCancel={() => setShowForm(false)}
        >
            {isGlobalLoading && (
                <div className="absolute -top-2 left-0 w-full h-full bg-black/15 z-10 rounded-lg flex justify-center items-center">
                    <div className="flex flex-col justify-center items-center gap-1">
                        <div className="size-6 rounded-full border-2 border-white !border-t-primary animate-spin" />
                    </div>
                </div>
            )}

            <CommonTabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                className="mt-2"
            />
        </CommonDialog>
    );
};

export default EnhancedAddAutoresponders;